import { Suspense, lazy, type ReactNode } from 'react';
import { NavLink, Route, Routes } from 'react-router-dom';
import { AppDataProvider, useAppData } from './AppData.js';
import { AuthProvider, useAuth } from './Auth.js';
import { ToastProvider } from './components/ui.js';
import { HomePage } from './pages/HomePage.js';
import { ProductsPage } from './pages/ProductsPage.js';
import { QuickEntryPage } from './pages/QuickEntryPage.js';
import { TurboPage } from './pages/TurboPage.js';
import { StoragePage } from './pages/StoragePage.js';
import { ShoppingPage } from './pages/ShoppingPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { AdminPage } from './pages/AdminPage.js';
import { AppPage } from './pages/AppPage.js';

// A biblioteca de decodificacao pesa mais que o resto do app somado: so entra
// quando o usuario abre o scanner.
const ScanPage = lazy(() => import('./pages/ScanPage.js').then((m) => ({ default: m.ScanPage })));
// Mesmo motivo da tela de scanner: a conferencia tambem abre a camera.
const InventoryPage = lazy(() =>
  import('./pages/InventoryPage.js').then((m) => ({ default: m.InventoryPage })),
);

/** Espera enquanto a tela da camera baixa. */
function AbrindoScanner({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <main className="page">
          <p className="muted">Abrindo o scanner…</p>
        </main>
      }
    >
      {children}
    </Suspense>
  );
}

/**
 * Logotipo do Almox desenhado em SVG: o cubo isometrico ocupa o lugar do "o" e
 * o traco azul passa por baixo. Vetor em vez de imagem — fica nitido em
 * qualquer tela e acompanha o tema claro/escuro pelas variaveis de cor.
 */
function Brand() {
  const { logoUrl } = useAuth();
  // Logo enviado na administracao substitui o logotipo desenhado.
  if (logoUrl) return <img src={logoUrl} alt="Almox" className="brand-logo" />;
  return (
    <span>
      <span className="brand-word">
        Alm
        <svg className="brand-mark" viewBox="0 0 64 64" aria-hidden="true" focusable="false">
          <circle cx="32" cy="32" r="32" fill="currentColor" />
          <g fill="var(--surface)">
            <path d="M32.00,17.28 L43.64,24.00 L32.00,30.72 L20.36,24.00 Z" />
            <path d="M44.75,25.92 L44.75,39.36 L33.11,46.08 L33.11,32.64 Z" />
            <path d="M19.25,25.92 L30.89,32.64 L30.89,46.08 L19.25,39.36 Z" />
          </g>
        </svg>
        x
      </span>
      <svg className="brand-swoosh" viewBox="0 0 100 8" preserveAspectRatio="none" aria-hidden="true" focusable="false">
        <path
          d="M6,2.5 Q50,11 94,2.5"
          fill="none"
          stroke="var(--brand-accent)"
          strokeWidth="3.4"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}

/**
 * Navegacao do app. No desktop tudo cabe na barra de cima; no celular os quatro
 * destinos do dia a dia viram uma barra inferior de abas (polegar alcanca) e o
 * resto fica em Configuracoes. Sem isso, seis links quebravam em tres linhas
 * por cima do logotipo.
 */
interface Destino {
  to: string;
  end?: boolean;
  icon: string;
  label: string;
  /** Rotulo curto da barra inferior, onde nao cabe "Lista de compras". */
  short: string;
  badge?: 'alerts' | 'pending';
  /** Fora das abas do celular: acessado por Configuracoes. */
  desktopOnly?: boolean;
}

const DESTINOS: Destino[] = [
  { to: '/', end: true, icon: '🗄', label: 'Dispensas', short: 'Dispensas', badge: 'alerts' },
  { to: '/escanear', icon: '📷', label: 'Escanear', short: 'Escanear' },
  { to: '/turbo', icon: '⚡', label: 'Turbo', short: 'Turbo' },
  { to: '/produtos', icon: '🏷', label: 'Produtos', short: 'Produtos' },
  { to: '/lista-de-compras', icon: '🛒', label: 'Lista de compras', short: 'Compras', badge: 'pending' },
  { to: '/configuracoes', icon: '⚙️', label: 'Configuracoes', short: 'Ajustes' },
  { to: '/app', icon: '📱', label: 'App', short: 'App', desktopOnly: true },
  { to: '/admin', icon: '🔒', label: 'Admin', short: 'Admin', desktopOnly: true },
];

function TopBar() {
  const { pendingCount, alertCount, online } = useAppData();

  const contador = (destino: Destino): number =>
    destino.badge === 'alerts' ? alertCount : destino.badge === 'pending' ? pendingCount : 0;

  return (
    <>
      <header className="topbar">
        <NavLink to="/" className="brand" aria-label="Almox — inicio">
          <Brand />
        </NavLink>
        <nav className="nav">
          {DESTINOS.map((destino) => (
            <NavLink key={destino.to} to={destino.to} end={destino.end}>
              {destino.label}
              {contador(destino) > 0 ? <span className="count">{contador(destino)}</span> : null}
            </NavLink>
          ))}
        </nav>
      </header>
      {!online ? (
        <div className="offline-bar">
          Sem conexao com o servidor do Almox — os dados podem estar desatualizados.
        </div>
      ) : null}

      <nav className="tabbar" aria-label="Navegacao principal">
        {DESTINOS.filter((destino) => !destino.desktopOnly).map((destino) => (
          <NavLink key={destino.to} to={destino.to} end={destino.end}>
            <span className="tab-icon" aria-hidden="true">
              {destino.icon}
              {contador(destino) > 0 ? <span className="tab-count">{contador(destino)}</span> : null}
            </span>
            <span className="tab-label">{destino.short}</span>
          </NavLink>
        ))}
      </nav>
    </>
  );
}

export function App() {
  return (
    <ToastProvider>
      <AuthProvider>
        <AppDataProvider>
          <div className="app">
            <TopBar />
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/dispensa/:id" element={<StoragePage />} />
              <Route
                path="/escanear"
                element={
                  <AbrindoScanner>
                    <ScanPage />
                  </AbrindoScanner>
                }
              />
              <Route path="/turbo" element={<TurboPage />} />
              <Route
                path="/inventario"
                element={
                  <AbrindoScanner>
                    <InventoryPage />
                  </AbrindoScanner>
                }
              />
              <Route
                path="/inventario/:id"
                element={
                  <AbrindoScanner>
                    <InventoryPage />
                  </AbrindoScanner>
                }
              />
              <Route path="/sem-codigo" element={<QuickEntryPage />} />
              <Route path="/produtos" element={<ProductsPage />} />
              <Route path="/lista-de-compras" element={<ShoppingPage />} />
              <Route path="/configuracoes" element={<SettingsPage />} />
              <Route path="/app" element={<AppPage />} />
              <Route path="/admin" element={<AdminPage />} />
              <Route
                path="*"
                element={
                  <main className="page">
                    <h1>Pagina nao encontrada</h1>
                  </main>
                }
              />
            </Routes>
          </div>
        </AppDataProvider>
      </AuthProvider>
    </ToastProvider>
  );
}
